import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import "./css/Login.css";
import { NavLink } from "react-router-dom";
import { ROUTES } from "../routes/routes";
import { Api } from "../api";
import { AxiosError } from "axios";

function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error and resend button when user types
    if (error) {
      setError(null);
      setShowResendVerification(false);
    }
    if (success) {
      setSuccess(null);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError("Please enter your email address first");
      return;
    }

    setIsResending(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await Api.post("auth/resend-verification-email", { email: formData.email });
      if (response.status === 204) {
        setSuccess("Verification email sent! Please check your inbox.");
        setShowResendVerification(false);
      }
    } catch (error) {
      // Always show success for security (don't reveal if email exists)
      setSuccess("If an account exists with this email and is unverified, a verification email has been sent.");
      setShowResendVerification(false);
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await Api.post("auth/login", formData);
      if (response.status === 200) {
        login(response.data.tokens.access.token, response.data.user);
        // Save refresh token
        if (response.data.tokens.refresh?.token) {
          localStorage.setItem("refreshToken", response.data.tokens.refresh.token);
        }
        navigate("/");
      } else {
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || "An error occurred during login";
        setError(errorMessage);
        
        // Check if error is about email verification
        if (error.response?.status === 403 && errorMessage.includes("verify your email")) {
          // Automatically send verification code when user tries to login with unverified email
          if (formData.email) {
            // Store email for verification page
            localStorage.setItem('pendingVerificationEmail', formData.email);
            try {
              await Api.post("auth/resend-verification-email", { email: formData.email });
              setSuccess("A verification code has been sent to your email. Please check your inbox.");
            } catch (resendError) {
              // Even if resend fails, show the verification options
              setSuccess("Please verify your email to continue. A verification code has been sent.");
            }
          }
          setShowResendVerification(true);
        } else {
          setShowResendVerification(false);
        }
      } else {
        setError("An error occurred during login");
        setShowResendVerification(false);
      }
    }
  };

  return (
    <form className="login-form" onSubmit={handleSubmit}>
      <div className="login-form-header">
        <h2 className="login-form-title">Log in</h2>
      </div>
      <label htmlFor="email" className="login-form-label">
        Email
      </label>
      <input
        id="email"
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        className="login-form-input"
      />
      <label htmlFor="password" className="login-form-label">
        Password
      </label>
      <input
        id="password"
        type="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        className="login-form-input"
      />

      {error && <p className="login-form-error">{error}</p>}
      {success && <p className="login-form-success">{success}</p>}

      {showResendVerification && (
        <div className="resend-verification-container">
          <button
            type="button"
            onClick={() => {
              localStorage.setItem('pendingVerificationEmail', formData.email);
              navigate(ROUTES.VerifyEmail(), { 
                state: { email: formData.email }
              });
            }}
            className="resend-verification-button primary"
          >
            Enter Verification Code
          </button>
          <button
            type="button"
            onClick={handleResendVerification}
            disabled={isResending}
            className="resend-verification-button secondary"
          >
            {isResending ? "Sending..." : "Resend Code"}
          </button>
        </div>
      )}

      <button type="submit" className="login-form-button">
        Log in
      </button>
      <div className="login-form-footer">
        <NavLink to={ROUTES.ForgotPassword()} className="login-form-link left-link">
          Forgot your password?
        </NavLink>
        <NavLink to={ROUTES.SignUp()} className="login-form-link right-link">
          Sign Up
        </NavLink>
      </div>
    </form>
  );
}

export default Login;
